import React from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Stack,
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  Add as AddIcon,
  FilterList as FilterIcon,
  EventBusy as NoEventsIcon,
} from '@mui/icons-material';

interface EmptyCalendarStateProps {
  type: 'no-listings' | 'no-selected-listings' | 'no-bookings';
  currentMonth?: Date;
  onCreateListing?: () => void;
}

export const EmptyCalendarState: React.FC<EmptyCalendarStateProps> = ({
  type,
  currentMonth,
  onCreateListing,
}) => {
  const getContent = () => {
    switch (type) {
      case 'no-listings':
        return {
          icon: <CalendarIcon sx={{ fontSize: 64, color: 'text.secondary' }} />,
          title: 'No Listings Found',
          description: 'You haven\'t created any listings yet. Create your first listing to start managing bookings.',
          action: onCreateListing && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={onCreateListing}
            >
              Create Your First Listing
            </Button>
          ),
        };
      
      case 'no-selected-listings':
        return {
          icon: <FilterIcon sx={{ fontSize: 64, color: 'text.secondary' }} />,
          title: 'No Listings Selected',
          description: 'Please select one or more listings from the filter above to view their bookings on the calendar.',
          action: null,
        };
      
      case 'no-bookings':
        return {
          icon: <NoEventsIcon sx={{ fontSize: 64, color: 'text.secondary' }} />,
          title: 'No Bookings Found',
          description: currentMonth 
            ? `No bookings found for ${currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}. Your calendar is clear for this month.`
            : 'No bookings found for the selected listings and date range.',
          action: null,
        };
      
      default:
        return {
          icon: <CalendarIcon sx={{ fontSize: 64, color: 'text.secondary' }} />,
          title: 'Calendar',
          description: 'Your calendar view will appear here.',
          action: null,
        };
    }
  };

  const { icon, title, description, action } = getContent();

  return (
    <Card>
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            py: 6,
            px: 3,
          }}
        >
          {icon}
          
          <Typography variant="h6" sx={{ mt: 2, mb: 1, fontWeight: 600 }}>
            {title}
          </Typography>
          
          <Typography 
            variant="body2" 
            color="text.secondary" 
            sx={{ mb: 3, maxWidth: 400 }}
          >
            {description}
          </Typography>
          
          {action && (
            <Stack direction="row" spacing={2}>
              {action}
            </Stack>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default EmptyCalendarState;